import "rsuite/dist/rsuite.css";
import React, { useState, useEffect } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Container, Row, Col, Table, Button, Form, Modal, Badge } from 'react-bootstrap';
import { DatePicker } from 'rsuite';
import { useNavigate } from 'react-router-dom';

const STAGE_LABELS = {
    draft: 'Draft', poSent: 'PO Sent', confirmed: 'Confirmed', piReceived: 'PI Received', piApproved: 'PI Approved',
    lcRequested: 'LC Requested', draftLcApproved: 'Draft LC Approved', marginPaid: 'Margin Paid', lcOpened: 'LC Opened',
    production: 'In Production', shipped: 'Shipped', originals: 'Originals at Bank', retired: 'Retired',
    withCF: 'With C&F', cleared: 'Cleared', onTruck: 'On Truck', received: 'Received', closed: 'Closed',
};
const stageLabel = (s) => STAGE_LABELS[s] || s;

function emptyLine(category) {
    return category === 'Direct'
        ? { gsmId: '', gsmName: '', widthInch: '', qty: '', unit: 'MT', unitPrice: '' }
        : { materialId: '', materialName: '', qty: '', unit: 'kg', unitPrice: '' };
}

function PurchaseBoard() {
    const navigate = useNavigate();
    const [purchases, setPurchases] = useState([]);
    const [filters, setFilters] = useState({ category: '', type: '', q: '', overdueOnly: false });

    const [showNew, setShowNew] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [gsmOptions, setGsmOptions] = useState([]);
    const [materialOptions, setMaterialOptions] = useState([]);
    const [newPurchase, setNewPurchase] = useState({
        purchaseCategory: 'Direct', purchaseType: 'Local', vendorId: '', podate: new Date(),
        currency: 'BDT', incoterm: '', portOfLoading: '', portOfDischarge: '', paymentTerm: '', transportCost: '', remarks: '',
    });
    const [lines, setLines] = useState([emptyLine('Direct')]);

    useEffect(() => { fetchPurchases(); }, [filters]);

    const fetchPurchases = async () => {
        try {
            const params = {};
            if (filters.category) params.category = filters.category;
            if (filters.type) params.type = filters.type;
            if (filters.q) params.q = filters.q;
            if (filters.overdueOnly) params.overdueOnly = 1;
            const res = await axios.get(`${window.backendUrl}/purchases`, { params });
            setPurchases(res.data.data || []);
        } catch (err) {
            console.error('Error fetching purchases:', err);
        }
    };

    const fetchGsm = async () => {
        try {
            const res = await axios.get(`${window.backendUrl}/get_all_gsm`);
            setGsmOptions(res.data.data || []);
        } catch (err) { console.error(err); }
    };

    const fetchMaterials = async () => {
        try {
            const res = await axios.get(`${window.backendUrl}/indirect_materials`, { params: { active: 1 } });
            setMaterialOptions(res.data.data || []);
        } catch (err) { console.error(err); }
    };

    const fetchVendors = async (category) => {
        try {
            const res = await axios.get(`${window.backendUrl}/purchase_vendors`, { params: { type: 'Supplier', category, active: 1 } });
            setVendors(res.data.data || []);
        } catch (err) { console.error(err); }
    };

    const openNewPurchase = () => {
        setNewPurchase({ purchaseCategory: 'Direct', purchaseType: 'Local', vendorId: '', podate: new Date(), currency: 'BDT', incoterm: '', portOfLoading: '', portOfDischarge: '', paymentTerm: '', transportCost: '', remarks: '' });
        setLines([emptyLine('Direct')]);
        fetchGsm();
        fetchMaterials();
        fetchVendors('Direct');
        setShowNew(true);
    };

    const handleCategoryChange = (category) => {
        setNewPurchase(prev => ({ ...prev, purchaseCategory: category, vendorId: '' }));
        setLines([emptyLine(category)]);
        fetchVendors(category);
    };

    const handleNpChange = (field, value) => setNewPurchase(prev => ({ ...prev, [field]: value }));

    const handleLineChange = (idx, field, value) => {
        const next = [...lines];
        next[idx] = { ...next[idx], [field]: value };
        if (field === 'gsmId') {
            const g = gsmOptions.find(o => String(o.GSMID) === String(value));
            next[idx].gsmName = g ? `${g.Name} ${g.GSMValue}` : '';
        }
        if (field === 'materialId') {
            const m = materialOptions.find(o => String(o.MaterialID) === String(value));
            next[idx].materialName = m ? m.Name : '';
            next[idx].unit = m ? m.UnitName : 'kg';
        }
        setLines(next);
    };

    const addLine = () => setLines([...lines, emptyLine(newPurchase.purchaseCategory)]);
    const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

    const isImport = newPurchase.purchaseType === 'Import';

    const handleCreatePurchase = async () => {
        if (!newPurchase.vendorId) { alert('Select a vendor'); return; }
        const validLines = lines.filter(l => (newPurchase.purchaseCategory === 'Direct' ? l.gsmId : l.materialId) && l.qty);
        if (validLines.length === 0) { alert('Add at least one complete line'); return; }
        const payload = {
            purchaseCategory: newPurchase.purchaseCategory,
            purchaseType: newPurchase.purchaseType,
            vendorId: newPurchase.vendorId,
            podate: newPurchase.podate ? new Date(newPurchase.podate).toISOString().slice(0, 10) : undefined,
            currency: newPurchase.currency,
            incoterm: isImport ? newPurchase.incoterm : undefined,
            portOfLoading: isImport ? newPurchase.portOfLoading : undefined,
            portOfDischarge: isImport ? newPurchase.portOfDischarge : undefined,
            paymentTerm: newPurchase.paymentTerm,
            transportCost: newPurchase.transportCost || undefined,
            remarks: newPurchase.remarks,
            lines: validLines.map(l => newPurchase.purchaseCategory === 'Direct'
                ? { gsmId: l.gsmId, widthInch: l.widthInch, qty: l.qty, unit: l.unit, unitPrice: l.unitPrice }
                : { materialId: l.materialId, qty: l.qty, unit: l.unit, unitPrice: l.unitPrice }),
        };
        try {
            const res = await axios.post(`${window.backendUrl}/purchases`, payload);
            setShowNew(false);
            navigate(`/purchase-file/${res.data.purchaseId}`);
        } catch (err) {
            console.error('Error creating purchase:', err);
            alert('Failed to create purchase.');
        }
    };

    const overdueBadge = (p) => {
        if (p.Stage === 'received' || p.Stage === 'closed') return null;
        if (p.DaysToArrival === null || p.DaysToArrival === undefined) return null;
        if (p.DaysToArrival < 0) return <Badge bg="danger" className="ms-2">Overdue {Math.abs(p.DaysToArrival)}d</Badge>;
        if (p.DaysToArrival <= 7) return <Badge bg="warning" text="dark" className="ms-2">{p.DaysToArrival}d left</Badge>;
        return null;
    };

    return (
        <>
            <NavigationBar />
            <Container fluid style={{ marginTop: 20 }}>
                <Row className="align-items-center mb-3">
                    <Col xs="auto"><h3>Purchase Board</h3></Col>
                    <Col className="text-end"><Button variant="primary" onClick={openNewPurchase}>+ New Purchase</Button></Col>
                </Row>
                <Row className="mb-3">
                    <Col md={2}>
                        <Form.Select value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })}>
                            <option value="">All categories</option>
                            <option value="Direct">Direct (paper)</option>
                            <option value="Indirect">Indirect</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Select value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })}>
                            <option value="">Import & Local</option>
                            <option value="Import">Import</option>
                            <option value="Local">Local</option>
                        </Form.Select>
                    </Col>
                    <Col md={3}>
                        <Form.Control placeholder="Search PO no. or vendor..." value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} />
                    </Col>
                    <Col md={3} className="d-flex align-items-center">
                        <Form.Check type="checkbox" label="Overdue only" checked={filters.overdueOnly} onChange={e => setFilters({ ...filters, overdueOnly: e.target.checked })} />
                    </Col>
                </Row>
                <Table striped bordered hover responsive size="sm">
                    <thead>
                        <tr>
                            <th>PO No.</th><th>Vendor</th><th>Category</th><th>Type</th><th>Stage</th>
                            <th>Expected arrival</th><th>Owner</th><th>Total</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {purchases.length === 0 ? (
                            <tr><td colSpan={9} className="text-center text-muted" style={{ padding: 30 }}>No purchases yet. Click "+ New Purchase" to start one.</td></tr>
                        ) : purchases.map(p => (
                            <tr key={p.PurchaseID} style={{ cursor: 'pointer' }} onClick={() => navigate(`/purchase-file/${p.PurchaseID}`)}>
                                <td>{p.PONo}</td>
                                <td>{p.VendorName}</td>
                                <td>{p.PurchaseCategory}</td>
                                <td>{p.PurchaseType}</td>
                                <td>
                                    <Badge bg={p.IsCancelled ? 'dark' : p.OnHold ? 'secondary' : 'primary'}>{stageLabel(p.Stage)}</Badge>
                                    {overdueBadge(p)}
                                </td>
                                <td>{p.ExpectedArrivalDate ? String(p.ExpectedArrivalDate).slice(0, 10) : '-'}</td>
                                <td>{p.OwnerName || '-'}</td>
                                <td>{p.Currency} {Number(p.TotalAmount).toLocaleString()}</td>
                                <td><Button size="sm" variant="outline-primary">Open</Button></td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Container>

            <Modal show={showNew} onHide={() => setShowNew(false)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>New Purchase</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Label>Category</Form.Label>
                            <Form.Select value={newPurchase.purchaseCategory} onChange={e => handleCategoryChange(e.target.value)}>
                                <option value="Direct">Direct (paper)</option>
                                <option value="Indirect">Indirect (other materials)</option>
                            </Form.Select>
                        </Col>
                        <Col md={4}>
                            <Form.Label>Type</Form.Label>
                            <Form.Select value={newPurchase.purchaseType} onChange={e => handleNpChange('purchaseType', e.target.value)}>
                                <option value="Local">Local</option>
                                <option value="Import">Import</option>
                            </Form.Select>
                        </Col>
                        <Col md={4}>
                            <Form.Label>PO Date</Form.Label>
                            <DatePicker value={newPurchase.podate} onChange={v => handleNpChange('podate', v)} oneTap style={{ width: '100%' }} />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Label>Vendor</Form.Label>
                            <Form.Select value={newPurchase.vendorId} onChange={e => handleNpChange('vendorId', e.target.value)}>
                                <option value="">Select vendor</option>
                                {vendors.map(v => <option key={v.VendorID} value={v.VendorID}>{v.Name}{v.LeadTimeDays ? ` (lead time ${v.LeadTimeDays}d)` : ''}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label>Currency</Form.Label>
                            <Form.Control value={newPurchase.currency} onChange={e => handleNpChange('currency', e.target.value)} />
                        </Col>
                        <Col md={3}>
                            <Form.Label>Payment term</Form.Label>
                            <Form.Control value={newPurchase.paymentTerm} onChange={e => handleNpChange('paymentTerm', e.target.value)} />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={3}>
                            <Form.Label>Transport cost</Form.Label>
                            <Form.Control type="number" min="0" value={newPurchase.transportCost} onChange={e => handleNpChange('transportCost', e.target.value)} placeholder="0" />
                        </Col>
                    </Row>
                    {isImport && (
                        <Row className="mb-3">
                            <Col md={4}><Form.Label>Incoterm</Form.Label><Form.Control value={newPurchase.incoterm} onChange={e => handleNpChange('incoterm', e.target.value)} placeholder="CFR / FOB" /></Col>
                            <Col md={4}><Form.Label>Port of loading</Form.Label><Form.Control value={newPurchase.portOfLoading} onChange={e => handleNpChange('portOfLoading', e.target.value)} /></Col>
                            <Col md={4}><Form.Label>Port of discharge</Form.Label><Form.Control value={newPurchase.portOfDischarge} onChange={e => handleNpChange('portOfDischarge', e.target.value)} placeholder="Chattogram" /></Col>
                        </Row>
                    )}

                    <hr />
                    <Row className="align-items-center mb-2">
                        <Col><strong>Items</strong></Col>
                        <Col className="text-end"><Button size="sm" onClick={addLine}>+ Add Line</Button></Col>
                    </Row>
                    <Table size="sm" bordered>
                        <thead>
                            <tr>
                                <th>{newPurchase.purchaseCategory === 'Direct' ? 'Paper' : 'Material'}</th>
                                {newPurchase.purchaseCategory === 'Direct' && <th>Width (inch)</th>}
                                <th>Qty</th><th>Unit</th><th>Unit price</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((l, idx) => (
                                <tr key={idx}>
                                    <td>
                                        {newPurchase.purchaseCategory === 'Direct' ? (
                                            <Form.Select size="sm" value={l.gsmId} onChange={e => handleLineChange(idx, 'gsmId', e.target.value)}>
                                                <option value="">Select paper</option>
                                                {gsmOptions.map(o => <option key={o.GSMID} value={o.GSMID}>{o.Name} {o.GSMValue}</option>)}
                                            </Form.Select>
                                        ) : (
                                            <Form.Select size="sm" value={l.materialId} onChange={e => handleLineChange(idx, 'materialId', e.target.value)}>
                                                <option value="">Select material</option>
                                                {materialOptions.map(o => <option key={o.MaterialID} value={o.MaterialID}>{o.Name}</option>)}
                                            </Form.Select>
                                        )}
                                    </td>
                                    {newPurchase.purchaseCategory === 'Direct' && (
                                        <td><Form.Control size="sm" value={l.widthInch} onChange={e => handleLineChange(idx, 'widthInch', e.target.value)} /></td>
                                    )}
                                    <td><Form.Control size="sm" value={l.qty} onChange={e => handleLineChange(idx, 'qty', e.target.value)} /></td>
                                    <td><Form.Control size="sm" value={l.unit} onChange={e => handleLineChange(idx, 'unit', e.target.value)} /></td>
                                    <td><Form.Control size="sm" value={l.unitPrice} onChange={e => handleLineChange(idx, 'unitPrice', e.target.value)} /></td>
                                    <td><i className="fa fa-trash text-danger" style={{ cursor: 'pointer' }} onClick={() => removeLine(idx)}></i></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Form.Group className="mb-2">
                        <Form.Label>Remarks</Form.Label>
                        <Form.Control as="textarea" rows={2} value={newPurchase.remarks} onChange={e => handleNpChange('remarks', e.target.value)} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowNew(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleCreatePurchase}>Create Purchase</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default PurchaseBoard;
